import { FC, useEffect, useMemo } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";

import { SpinnerFullPage } from "src/core/components/custom-ui/spinner-full-page";
import { ROUTE_PATH } from "src/core/constants/routePath";
import { useTenant } from "src/core/contexts/TenantContext/TenantProvider";
import { TENANT_STATUS, TenantRouteParams } from "src/core/types/tenant";
import { tenantPaths } from "src/core/utils/route";

/**
 * TenantParamGuard : protège toutes les routes "tenant-scoped".
 * - Valide la présence de :tenantId dans l'URL
 * - Vérifie que ce tenantId fait partie des tenants accessibles
 * - Synchronise le tenant du contexte avec celui de l'URL (source de vérité = URL)
 * - Bloque le rendu tant que la synchronisation n'est pas terminée
 */
export const TenantParamGuard: FC = () => {
  const { tenantId: tenantIdFromUrl } = useParams<TenantRouteParams>();

  const { tenantId: tenantIdFromContext, availableTenants, status, switchTenant } = useTenant();

  /**
   * IMPORTANT : tous les hooks doivent être appelés à chaque rendu,
   * donc on calcule ici nos flags même si tenantIdFromUrl est undefined.
   */
  const isTenantAllowed = useMemo(() => {
    if (!tenantIdFromUrl) return false;
    return availableTenants.some((t) => t.id === tenantIdFromUrl);
  }, [availableTenants, tenantIdFromUrl]);

  const mustSyncTenantFromUrl = Boolean(tenantIdFromUrl) && tenantIdFromContext !== tenantIdFromUrl;

  /**
   * Synchronisation du contexte de tenant avec l'URL.
   * - L'URL est la source de vérité (utile pour le refresh / deep-link / partage d'URL).
   * - On protège l'effet avec des conditions, mais le hook est toujours appelé.
   */
  useEffect(() => {
    if (!tenantIdFromUrl) return;
    if (!isTenantAllowed) return;
    if (!mustSyncTenantFromUrl) return;

    void switchTenant(tenantIdFromUrl);
  }, [isTenantAllowed, mustSyncTenantFromUrl, switchTenant, tenantIdFromUrl]);

  // 1) URL invalide : pas de tenantId => 404 global (route non conforme)
  if (!tenantIdFromUrl) {
    console.warn("[NAV] TenantParamGuard -> ERROR_404 (missing tenantId)");
    return <Navigate to={ROUTE_PATH.ERROR_404} replace />;
  }

  // 2) Tenant URL non autorisé : on renvoie vers "/" pour laisser RootRedirect choisir si possible
  if (!isTenantAllowed) {
    console.warn("[NAV] TenantParamGuard -> redirect because tenant not allowed", {
      tenantIdFromUrl,
      availableTenants,
    });

    // On repart de la racine pour laisser RootRedirect choisir un tenant valide.
    // (TenantsValidationGate garantit déjà qu'on a au moins un tenant accessible.)
    return <Navigate to="/" replace />;
  }

  /**
   * 3) On bloque tant que le tenant n'a jamais été résolu.
   * Objectif : éviter de rendre l'app sans tenantId côté contexte, alors que le back l'exige.
   */
  const isFirstTenantResolution = status === TENANT_STATUS.INITIALIZING && !tenantIdFromContext;
  if (isFirstTenantResolution) {
    return <SpinnerFullPage />;
  }

  // 4) Si l'URL et le contexte diffèrent, on attend la synchronisation pour éviter du "flicker"
  if (mustSyncTenantFromUrl) {
    return <SpinnerFullPage />;
  }

  // 5) Sécurité : URL OK, auth OK, tenant autorisé mais contexte vide => incohérence => 403 tenant-scoped
  if (!tenantIdFromContext) {
    console.warn("[NAV] TenantParamGuard -> tenant context missing");
    return <Navigate to={tenantPaths.error403(tenantIdFromUrl)} replace />;
  }

  return <Outlet />;
};
